import { Dtos, FileType } from "@inrev/common";
import {
	useFetchAdminBondIssuance,
	useUploadCompletedBondForm,
} from "@portal/api/bond-request/admin";
import {
	AdminApproveBondFormForInRevIssueFormData,
	AdminBondRequest,
} from "@portal/api/bond-request/admin/types";
import {
	FormField,
	FormFieldGroup,
	FormItemLabel,
	FormRow,
	FormSection,
} from "@portal/components/form";
import {
	FormDatePicker,
	FormDropdown,
	FormInput,
	FormStateDropdown,
	FormYesNo,
} from "@portal/components/form/fields";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Button } from "@portal/components/ui/Button";
import { LocalFileUpload } from "@portal/components/ui/LocalFileUpload";
import { NotificationCard } from "@portal/components/ui/NotificationCard";
import { useObligeeRoleOptions } from "@portal/hooks/useObligeeRoleOptions";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { NameSuffix, UploadFile } from "../../../types";
import { stripEmptyResolver, typedZodResolver } from "../../../utils/form";

type AdminApproveBondFormForInRevIssueFormProps = {
	request: AdminBondRequest;
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response;
};

const getDefaultFormData = (
	request: AdminApproveBondFormForInRevIssueFormProps["request"],
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response,
): AdminApproveBondFormForInRevIssueFormData => ({
	suretyType: request.suretyType,
	contractSuretyType: request.contractSuretyType === "bid" ? "bid" : "final",
	bondFormType: bondForm.bondFormType ?? "",
	attributes: {
		cancellable:
			request.contractSuretyType === "bid" ? false : (bondForm.attributes.cancellable ?? ""),
		continuous:
			request.contractSuretyType === "bid" ? true : (bondForm.attributes.continuous ?? ""),
		renewable: false,
	},
	bondForm: {
		data: {
			bondNumber: bondForm.fillData.bondNumber ?? "",
			effectiveDate: bondForm.fillData.effectiveDate ?? "",
			contractDate:
				bondForm.contractSuretyType !== "bid" ? (bondForm.fillData.contractDate ?? "") : "",
			contractDescription:
				(bondForm.contractSuretyType !== "bid"
					? bondForm.fillData.contractDescription
					: undefined) ??
				request.project.name ??
				"",
			bidDate: request.contractSuretyType === "bid" ? (request.bidDate ?? "") : "",
			projectDescription:
				(bondForm.contractSuretyType === "bid"
					? bondForm.fillData.projectDescription
					: undefined) ??
				request.project.name ??
				"",
			obligees: [
				{
					name: bondForm.fillData.obligees?.[0]?.name ?? "",
					address: bondForm.fillData.obligees?.[0]?.address || {
						street: "",
						city: "",
						state: "",
						zip: "",
					},
					role: bondForm.fillData.obligees?.[0].role ?? "",
				},
			],
			principalSigner: {
				firstName: "",
				lastName: "",
				suffix: "",
				title: "",
			},
		},
	},
});

const _AdminCompleteInRevIssuedBondFormForm = ({
	request,
	bondForm,
}: AdminApproveBondFormForInRevIssueFormProps) => {
	const obligeeRoleOptions = useObligeeRoleOptions();
	const formMethods = useForm<
		AdminApproveBondFormForInRevIssueFormData,
		any,
		Dtos.Admin.SuretyBondForm.Upload.Request
	>({
		defaultValues: getDefaultFormData(request, bondForm),
		reValidateMode: "onBlur",
		resolver: stripEmptyResolver(
			typedZodResolver(Dtos.Admin.SuretyBondForm.Upload.Request.schema),
			true,
		),
	});
	const [bondFormFileUpload, setBondFormFileUpload] = useState<
		UploadFile<typeof FileType.filled_bond_form>[]
	>([]);

	const { uploadCompletedBondForm, uploadCompletedBondFormIsLoading } = useUploadCompletedBondForm(
		request.id,
		request.suretyAccountId,
	);

	const handleSubmit = (data: Dtos.Admin.SuretyBondForm.Upload.Request) => {
		uploadCompletedBondForm(data);
	};

	const bondFormTypeOptions = useMemo(() => {
		if (request.contractSuretyType === "bid") {
			return [{ label: "Bid", value: "bid" }];
		}
		return [
			{ label: "Performance", value: "performance" },
			{ label: "Payment", value: "payment" },
			{ label: "Performance and Payment", value: "pnp" },
		];
	}, [request.contractSuretyType]);

	return (
		<form className="flex flex-col space-y-[35px]">
			<NotificationCard
				type="action"
				text={
					bondForm.filledWithAdornmentsFileId
						? "Upload a new bond form to replace the existing one"
						: `Provide the following details and upload a copy of the bond form for ${bondForm.issuanceMethod === "electronic" ? "electronic" : "inRev"} issue`
				}
			/>
			<FormFieldGroup>
				<FormRow>
					<FormField condensed>
						<FormItemLabel required>Bond Number</FormItemLabel>
						<FormInput
							control={formMethods.control}
							name={`bondForm.data.bondNumber`}
							className="bg-white"
							compact
						/>
					</FormField>
					<FormField condensed>
						<FormItemLabel required>Effective Date</FormItemLabel>
						<FormDatePicker
							name="bondForm.data.effectiveDate"
							control={formMethods.control}
							compact
						/>
					</FormField>
					{(request.contractSuretyType === "final" ||
						request.contractSuretyType === "bid_to_final") && (
						<FormField condensed>
							<FormItemLabel condensed required>
								Contract Date
							</FormItemLabel>
							<FormDatePicker
								control={formMethods.control}
								name={`bondForm.data.contractDate`}
								className="bg-white"
								compact
							/>
						</FormField>
					)}
					{request.contractSuretyType === "bid" && (
						<FormField condensed>
							<FormItemLabel condensed required>
								Bid Date
							</FormItemLabel>
							<FormDatePicker
								control={formMethods.control}
								name={`bondForm.data.bidDate`}
								className="bg-white"
								compact
							/>
						</FormField>
					)}
				</FormRow>
				<FormField>
					<FormItemLabel required>Description</FormItemLabel>
					<span className="!my-[5px] text-[14px] text-tertiary italic">
						As it appears on the bond form
					</span>
					<FormInput
						control={formMethods.control}
						name={
							request.contractSuretyType === "bid"
								? `bondForm.data.projectDescription`
								: `bondForm.data.contractDescription`
						}
						className="bg-white"
						compact
					/>
				</FormField>
			</FormFieldGroup>
			<FormSection header="Obligee">
				<FormFieldGroup condensed>
					<FormRow>
						<FormField condensed>
							<FormItemLabel condensed required>
								Name
							</FormItemLabel>
							<FormInput
								control={formMethods.control}
								name={`bondForm.data.obligees.0.name`}
								className="bg-white"
								compact
							/>
						</FormField>
						<FormField condensed className="w-[50px] max-w-[225px]">
							<FormItemLabel condensed required>
								Role
							</FormItemLabel>
							<FormDropdown
								control={formMethods.control}
								name={"bondForm.data.obligees.0.role"}
								buttonClassName="bg-white"
								options={obligeeRoleOptions}
								placeholder="Select one"
								errorMessage="Invalid role"
								compact
							/>
						</FormField>
					</FormRow>
					<FormField condensed>
						<FormItemLabel condensed required>
							Street
						</FormItemLabel>
						<FormInput
							control={formMethods.control}
							name={`bondForm.data.obligees.0.address.street`}
							className="bg-white"
							compact
						/>
						<FormRow>
							<FormField condensed>
								<FormItemLabel condensed required>
									City
								</FormItemLabel>
								<FormInput
									control={formMethods.control}
									name={`bondForm.data.obligees.0.address.city`}
									className="bg-white"
									compact
								/>
							</FormField>
							<FormField condensed className="w-[100px] max-w-[100px]">
								<FormItemLabel condensed required>
									State
								</FormItemLabel>
								<FormStateDropdown
									control={formMethods.control}
									name={`bondForm.data.obligees.0.address.state`}
									buttonClassName="bg-white"
									optionsClassName="max-h-[230px] overflow-y-auto"
									errorMessage="Invalid state"
									compact
									abbreviate
								/>
							</FormField>
							<FormField condensed className="w-[100px] max-w-[100px]">
								<FormItemLabel condensed required>
									Zip
								</FormItemLabel>
								<FormInput
									control={formMethods.control}
									name={`bondForm.data.obligees.0.address.zip`}
									className="bg-white"
									compact
								/>
							</FormField>
						</FormRow>
					</FormField>
				</FormFieldGroup>
			</FormSection>
			<FormSection
				header="Signer"
				subHeader="If available"
				subHeaderClassName="text-tertiary italic"
			>
				<FormFieldGroup condensed>
					<FormRow>
						<FormField condensed>
							<FormItemLabel condensed>First Name</FormItemLabel>
							<FormInput
								control={formMethods.control}
								name={`bondForm.data.principalSigner.firstName`}
								className="bg-white"
								compact
							/>
						</FormField>
						<FormField condensed>
							<FormItemLabel condensed>Last Name</FormItemLabel>
							<FormInput
								control={formMethods.control}
								name={`bondForm.data.principalSigner.lastName`}
								className="bg-white"
								compact
							/>
						</FormField>
						<FormField className="w-[100px] max-w-[100px]" condensed>
							<FormItemLabel condensed>Suffix</FormItemLabel>
							<FormDropdown
								control={formMethods.control}
								name="bondForm.data.principalSigner.suffix"
								options={Object.values(NameSuffix).map((suffix) => ({
									value: suffix,
									label: suffix,
								}))}
								buttonClassName="bg-white"
								compact
							/>
						</FormField>
					</FormRow>
					<FormField condensed>
						<FormItemLabel condensed>Title</FormItemLabel>
						<FormInput
							control={formMethods.control}
							name={`bondForm.data.principalSigner.title`}
							className="bg-white"
							compact
						/>
					</FormField>
				</FormFieldGroup>
			</FormSection>
			<FormFieldGroup>
				<FormRow>
					<FormField condensed>
						<FormItemLabel required>Bond Form Type</FormItemLabel>
						<FormDropdown
							name="bondFormType"
							control={formMethods.control}
							options={bondFormTypeOptions}
							readonly={request.contractSuretyType === "bid"}
							placeholder="Select one"
							compact
						/>
					</FormField>
				</FormRow>
				{request.contractSuretyType !== "bid" && (
					<FormRow className="space-x-[50px]">
						<FormField condensed className="max-w-fit">
							<FormItemLabel required>Continuous</FormItemLabel>
							<FormYesNo name="attributes.continuous" control={formMethods.control} compact />
						</FormField>
						<FormField condensed className="flex-2">
							<FormItemLabel required>Cancellable</FormItemLabel>
							<FormYesNo name="attributes.cancellable" control={formMethods.control} compact />
						</FormField>
					</FormRow>
				)}
			</FormFieldGroup>
			<LocalFileUpload
				value={bondFormFileUpload}
				onChange={(files) => {
					setBondFormFileUpload(files);
					formMethods.setValue("bondForm.fileId", files[0]?.id);
				}}
				allowDelete={true}
				allowedTypesAndLabels={{
					[FileType.filled_bond_form]: "Filled Bond Form",
				}}
				allowedExtensions={["pdf"]}
				requiredTypes={[FileType.filled_bond_form]}
				maxTypesPerFile={1}
				maxFiles={1}
				hideChecklist={true}
				showErrors={formMethods.formState.errors.bondForm?.fileId !== undefined}
				analyticsContext="bond-attachments"
			/>
			<ConfirmationModalClickWrapper
				shouldConfirm={true}
				message="Are you sure?"
				onConfirm={formMethods.handleSubmit(handleSubmit)}
			>
				<Button
					color="primary"
					className="max-w-fit self-center px-[50px] !mb-[20px] !mt[45px]"
					filled
				>
					Submit
				</Button>
			</ConfirmationModalClickWrapper>
			{uploadCompletedBondFormIsLoading && <LoadingModal />}
		</form>
	);
};

export const AdminCompleteInRevIssuedBondFormForm = ({
	request,
	bondForm,
}: AdminApproveBondFormForInRevIssueFormProps) => {
	const { bondIssuance } = useFetchAdminBondIssuance(request.suretyAccountId, request.id);

	if (
		bondIssuance &&
		bondIssuance.method !== "agent" &&
		(bondIssuance.pendingInRevIssue || bondForm.filledWithAdornmentsFileId)
	) {
		return <_AdminCompleteInRevIssuedBondFormForm request={request} bondForm={bondForm} />;
	}
	return <></>;
};
